import { useEffect, useMemo, useState } from "react";
import {
  Loader2, Inbox, Mail, Phone, Sparkles, Calendar, Webhook, Activity,
  Filter, ChevronDown, Search,
} from "lucide-react";
import { accessApi } from "@/access/api";
import { LeadDrawer } from "@/access/LeadDrawer";

/* Sales pipeline (Kanban) — only sales-workflow forms:
   lead · build_os · audit · integration_notify.
   Click a card → opens right-side LeadDrawer.
   Drag a card across columns → updates status.
   Status change auto-logs to the activity timeline. */

const COLS = [
  { id: "new",       label: "New",       accent: "from-violet-500/15 via-fuchsia-500/8",   dot: "bg-violet-400",   ring: "ring-violet-400/30"  },
  { id: "contacted", label: "Contacted", accent: "from-amber-500/15 via-orange-500/8",     dot: "bg-amber-400",    ring: "ring-amber-400/30"   },
  { id: "qualified", label: "Qualified", accent: "from-sky-500/15 via-blue-500/8",         dot: "bg-sky-400",      ring: "ring-sky-400/30"     },
  { id: "resolved",  label: "Resolved",  accent: "from-emerald-500/15 via-teal-500/8",     dot: "bg-emerald-400",  ring: "ring-emerald-400/30" },
];

const FORM_META = {
  lead:               { label: "Demo",      Icon: Calendar, color: "violet"  },
  build_os:           { label: "Build OS",  Icon: Sparkles, color: "blue"    },
  audit:              { label: "Audit",     Icon: Activity, color: "amber"   },
  integration_notify: { label: "EMR notify",Icon: Webhook,  color: "fuchsia" },
};

const FORM_BADGE = {
  violet:  "bg-violet-500/15 text-violet-200 border-violet-500/25",
  blue:    "bg-blue-500/15 text-blue-200 border-blue-500/25",
  amber:   "bg-amber-500/15 text-amber-200 border-amber-500/25",
  fuchsia: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/25",
};

export default function AccessLeads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [filterForm, setFilterForm] = useState("all");
  const [q, setQ] = useState("");

  const load = () =>
    accessApi.leads()
      .then(({ data }) => setRows(data))
      .finally(() => setLoading(false));

  useEffect(() => { setLoading(true); load(); }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (filterForm !== "all") out = out.filter((r) => r.form_type === filterForm);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((r) =>
        JSON.stringify({ d: r.data, a: r.attribution }).toLowerCase().includes(s)
      );
    }
    return out;
  }, [rows, filterForm, q]);

  const grouped = COLS.map((c) => ({
    ...c,
    items: filtered.filter((r) => r.status === c.id),
  }));

  const selected = selectedId ? rows.find((r) => r.id === selectedId) : null;

  const onCardDrop = async (colId, leadId) => {
    setDragOverCol(null);
    setDraggingId(null);
    const lead = rows.find((r) => r.id === leadId);
    if (!lead || lead.status === colId) return;
    // Optimistic
    setRows((prev) => prev.map((r) => (r.id === leadId ? { ...r, status: colId } : r)));
    try {
      const { data } = await accessApi.updateSubmission(leadId, { status: colId });
      setRows((prev) => prev.map((r) => (r.id === leadId ? data : r)));
    } catch {
      // rollback on failure
      setRows((prev) => prev.map((r) => (r.id === leadId ? lead : r)));
    }
  };

  const handleUpdate = (updated) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };
  const handleDelete = async (id) => {
    await accessApi.deleteSubmission(id);
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <div data-testid="access-leads" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-violet-300">Sales pipeline</p>
          <h1 className="mt-1.5 font-display text-3xl font-black tracking-tight sm:text-4xl">Leads</h1>
          <p className="mt-1.5 text-[13.5px] text-white/55">
            Demo requests, audits and EMR notifies. <span className="text-white/85 font-semibold">Drag</span> a card across columns or open it for the full timeline.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search leads…"
              data-testid="leads-search"
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] py-1.5 pl-8 pr-3 text-[12.5px] outline-none placeholder:text-white/30 focus:border-violet-400/60"
            />
          </div>
          <div className="relative">
            <Filter className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
            <select
              value={filterForm}
              onChange={(e) => setFilterForm(e.target.value)}
              data-testid="leads-form-filter"
              className="rounded-lg border border-white/[0.08] bg-white/[0.03] py-1.5 pl-8 pr-8 text-[12.5px] font-bold focus:border-violet-400/60 focus:outline-none appearance-none"
            >
              <option value="all" className="bg-[#0a0a14]">All forms</option>
              <option value="lead" className="bg-[#0a0a14]">Demo / lead</option>
              <option value="build_os" className="bg-[#0a0a14]">Build OS</option>
              <option value="audit" className="bg-[#0a0a14]">Audit</option>
              <option value="integration_notify" className="bg-[#0a0a14]">EMR notify</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
          </div>
        </div>
      </header>

      {/* Kanban */}
      <div className="-mx-5 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:px-8 lg:mx-0 lg:overflow-visible lg:px-0">
        <div className="flex gap-4 lg:grid lg:grid-cols-4 lg:gap-4">
          {grouped.map((col) => (
            <Column
              key={col.id}
              col={col}
              isDragOver={dragOverCol === col.id}
              onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id); }}
              onDragLeave={() => setDragOverCol((c) => (c === col.id ? null : c))}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData("text/plain");
                if (id) onCardDrop(col.id, id);
              }}
            >
              {col.items.map((r) => (
                <Card
                  key={r.id}
                  lead={r}
                  isDragging={draggingId === r.id}
                  onClick={() => setSelectedId(r.id)}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", r.id);
                    e.dataTransfer.effectAllowed = "move";
                    setDraggingId(r.id);
                  }}
                  onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
                />
              ))}
              {col.items.length === 0 && (
                <p className="rounded-xl border border-dashed border-white/[0.08] px-3 py-8 text-center text-[11.5px] text-white/35">
                  {col.id === "new" ? "New leads land here" : "Drop a card here"}
                </p>
              )}
            </Column>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 text-[12px] text-white/40">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading leads…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/10 px-6 py-12 text-center">
          <Inbox className="mx-auto h-6 w-6 text-white/30" />
          <p className="mt-3 text-[13px] text-white/55">
            No leads yet — once visitors fill the Demo, Build OS, Audit or EMR-notify forms, they&apos;ll land here.
          </p>
        </div>
      )}

      {/* Right-side detail drawer */}
      <LeadDrawer
        lead={selected}
        onClose={() => setSelectedId(null)}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </div>
  );
}

/* ---------------- column ---------------- */
const Column = ({ col, isDragOver, onDragOver, onDragLeave, onDrop, children }) => (
  <div
    data-testid={`leads-col-${col.id}`}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    className={`w-[88vw] flex-none rounded-2xl border bg-gradient-to-br ${col.accent} to-transparent p-3 transition-all sm:w-[44vw] lg:w-auto lg:flex-1 ${
      isDragOver ? `border-white/30 ring-2 ${col.ring}` : "border-white/[0.06]"
    }`}
  >
    <div className="mb-3 flex items-center justify-between px-1">
      <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/85">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${col.dot}`} />
        {col.label}
      </p>
      <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-md bg-white/[0.06] px-1.5 text-[10.5px] font-bold tabular-nums text-white/65">
        {Array.isArray(children) ? children.filter(Boolean).length : (children ? 1 : 0)}
      </span>
    </div>
    <div className="space-y-2 min-h-[140px]">{children}</div>
  </div>
);

/* ---------------- card ---------------- */
const Card = ({ lead, isDragging, onClick, onDragStart, onDragEnd }) => {
  const d = lead.data || {};
  const a = lead.attribution || {};
  const meta = FORM_META[lead.form_type] || FORM_META.lead;
  const acts = lead.activities || [];
  const lastAct = acts.length ? acts[acts.length - 1] : null;

  return (
    <article
      role="button"
      tabIndex={0}
      draggable
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`lead-card-${lead.id}`}
      className={`relative cursor-grab rounded-xl border border-white/[0.08] bg-[#0a0a14]/70 p-3.5 backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-white/[0.18] hover:bg-[#0a0a14]/90 active:cursor-grabbing ${
        isDragging ? "rotate-1 opacity-50 scale-[0.98]" : ""
      }`}
    >
      <header className="flex items-start justify-between gap-2">
        <p className="truncate font-display text-[14px] font-black tracking-tight">
          {d.name || d.email || "(anonymous)"}
        </p>
        <span className={`inline-flex flex-none items-center rounded-md border px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.14em] ${FORM_BADGE[meta.color]}`}>
          {meta.label}
        </span>
      </header>

      <p className="mt-0.5 truncate text-[11.5px] text-white/55">
        {d.clinic_name || d.email || "—"}
      </p>

      {/* Build-OS quick summary */}
      {lead.form_type === "build_os" && d.selected_base && (
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-blue-500/10 px-2 py-1 text-[11px]">
          <span className="font-bold text-blue-300">{d.selected_base}</span>
          {typeof d.total_monthly === "number" && (
            <span className="font-bold text-white">${d.total_monthly}/mo</span>
          )}
        </p>
      )}

      {/* Quick contact icons */}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-white/55">
        {d.email && (
          <span className="inline-flex items-center gap-1">
            <Mail className="h-3 w-3" /> {d.email.split("@")[0]}
          </span>
        )}
        {d.phone && d.phone !== "—" && (
          <span className="inline-flex items-center gap-1">
            <Phone className="h-3 w-3" /> {d.phone}
          </span>
        )}
      </div>

      {/* Last activity hint */}
      {lastAct && (
        <p className="mt-3 truncate rounded-md bg-white/[0.03] px-2 py-1 text-[10.5px] text-white/55">
          {lastAct.type === "status_change"
            ? `Moved to ${lastAct.to_status}`
            : `${lastAct.type.replace("note_", "")}: ${(lastAct.body || "").slice(0, 60)}${(lastAct.body || "").length > 60 ? "…" : ""}`
          }
        </p>
      )}

      {/* Footer — timestamp + activity count */}
      <footer className="mt-3 flex items-center justify-between text-[10px] text-white/35">
        <span>{new Date(lead.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
        {acts.length > 0 && (
          <span className="inline-flex items-center gap-1 text-violet-300/85">
            <Activity className="h-3 w-3" /> {acts.length}
          </span>
        )}
      </footer>
    </article>
  );
};
